import { State, Action, Selector } from '@ngxs/store';
import { OctroiApiService } from './services/octroi-api.service';
import { FormattedLocalisation } from '../shared/modeles/formatted-localisation.modele';
import { OctroiModele } from './modeles/octroiModele';
import { PreferenceModel } from '../core/modeles/personneModele';
import { Injectable } from '@angular/core';

enum OctroiStepsEnum {
  ACCUEIL_PRET = 'accueil-pret',
  ACCUEIL_ASSURANCE = 'accueil-assurance',
  RENDEZ_VOUS = 'rendez_vous',
  RECAP = 'recap',
  CONFIRMATION = 'confirmation'
}

const defaultState = {
  steps: [
    OctroiStepsEnum.ACCUEIL_PRET,
    OctroiStepsEnum.ACCUEIL_ASSURANCE,
    OctroiStepsEnum.RENDEZ_VOUS,
    OctroiStepsEnum.RECAP,
    OctroiStepsEnum.CONFIRMATION
  ],
  currentStep: OctroiStepsEnum.ACCUEIL_PRET,
  firstStep: null,
  indicateurCoemprunteur: null,
  codePeriodeAchat: null,
  numeroTelephone: null,
  disponibiliteAppel: [],
  courriel: null,
  codePostal: null,
  langue: null,
  notes: null,
  indicateurAcceptationOffreAchat: null,
  dateLimiteObtentionFinancement: null,
  dateAcceptationOffreAchat: null,
  isCompleted: false,
  lastOctroi: null,
  localisationRepresentant: null
};

export interface OctroiStateModel {
  steps: Array<string>;
  currentStep: string;
  firstStep: string;
  indicateurCoemprunteur: boolean;
  codePeriodeAchat: string;
  numeroTelephone: string;
  disponibiliteAppel: Array<string>;
  courriel: string;
  codePostal: string;
  langue: string;
  notes: string;
  indicateurAcceptationOffreAchat: boolean;
  dateLimiteObtentionFinancement: string;
  dateAcceptationOffreAchat: string;
  isCompleted: boolean;
  lastOctroi: OctroiModele;
  localisationRepresentant: FormattedLocalisation;
}

export class GoNextPage {
  static type = '[Octroi] GoNextPage';
}
export class SetFirstHomeStep {
  static type = '[Octroi] SetFirstHomeStep';
  constructor(public step: string) {}
}

export class GoPreviousPage {
  static type = '[Octroi] GoPreviousPage';
}

export class GoToPage {
  static type = '[Octroi] GoToPage';

  constructor(public page: string) {}
}

export class SubmitOctroi {
  static type = '[Octroi] SubmitOctroi';
}

export class SaveFormValues {
  static type = '[Octroi] SaveFormValues';

  constructor(public form: OctroiModele, public mode?: string) {}
}

export class ResetState {
  static type = '[Octroi] ResetState';
}

export class MarkAsCompleted {
  static type = '[Octroi] MarkAsCompleted';
}

export class LoadOctroi {
  static type = '[Octroi] LoadOctroi';
}
export class LoadPreferencesOctroi {
  static type = '[Octroi] LoadPreferences';
  constructor(public preferences: PreferenceModel, public localisationRepresentant: FormattedLocalisation = null) {}
}

export class FakeOctroiDebug {
  static type = '[Octroi] FakeOctroiDebug';
}

@Injectable()
@State<OctroiStateModel>({
  name: 'octroi',
  defaults: defaultState
})
export class OctroiState {
  constructor(private readonly _octroiApiService: OctroiApiService) {}

  @Selector()
  static donnees(state: OctroiStateModel) {
    return {
      indicateurCoemprunteur: state.indicateurCoemprunteur,
      codePeriodeAchat: state.codePeriodeAchat,
      indicateurAcceptationOffreAchat: state.indicateurAcceptationOffreAchat,
      numeroTelephone: state.numeroTelephone,
      disponibiliteAppel: state.disponibiliteAppel,
      courriel: state.courriel,
      codePostal: state.codePostal,
      langue: state.langue,
      notes: state.notes,
      dateLimiteObtentionFinancement: state.dateAcceptationOffreAchat,
      dateAcceptationOffreAchat: state.dateLimiteObtentionFinancement,
      localisationRepresentant: state.localisationRepresentant
    };
  }

  static scrollToTop() {
    const isWindowAvailable = typeof window !== 'undefined';

    if (isWindowAvailable) {
      window.scroll(0, 0);
    }
  }

  @Action(LoadPreferencesOctroi)
  loadPreferencesOctroi({ patchState }, { preferences, localisationRepresentant }: LoadPreferencesOctroi) {
    if (preferences) {
      patchState({
        numeroTelephone: preferences.telephone,
        courriel: preferences.courriel,
        codePostal: preferences.codePostal,
        langue: preferences.codeLangueCommunication || null,
        localisationRepresentant: localisationRepresentant
      });
    }
  }

  @Action(LoadOctroi)
  async loadOctroi({ patchState }) {
    const lastOctroi: OctroiModele = await this._octroiApiService.getOctroi().toPromise();

    patchState({
      isCompleted: lastOctroi ? true : false,
      lastOctroi: lastOctroi || null
    });
  }

  @Action(GoNextPage)
  goNextPage({ patchState, getState }) {
    const state = getState();
    const index = state.steps.indexOf(state.currentStep);
    let newStep = null;
    if (index < state.steps.length - 1) {
      if (state.currentStep === OctroiStepsEnum.ACCUEIL_PRET) {
        newStep = state.steps[index + 2];
      } else {
        newStep = state.steps[index + 1];
      }
      patchState({
        previousStep: state.currentStep,
        currentStep: newStep
      });
    }
    OctroiState.scrollToTop();
  }

  @Action(GoPreviousPage)
  goPreviousPage({ patchState, getState }) {
    const state = getState();
    const index = state.steps.indexOf(state.currentStep);
    let newStep = null;
    if (index > 0) {
      if (state.currentStep === OctroiStepsEnum.RENDEZ_VOUS && state.firstStep === OctroiStepsEnum.ACCUEIL_PRET) {
        newStep = state.steps[index - 2];
      } else {
        newStep = state.steps[index - 1];
      }
      patchState({
        currentStep: newStep
      });
    }

    OctroiState.scrollToTop();
  }

  @Action(GoToPage)
  goToPage({ getState, patchState }, { page }: GoToPage) {
    const state = getState();

    if (state.steps.includes(page)) {
      patchState({
        currentStep: page
      });
    }

    OctroiState.scrollToTop();
  }

  @Action(SaveFormValues)
  saveFormValues({ patchState, dispatch }, { form, mode }: SaveFormValues) {
    patchState({
      indicateurCoemprunteur: form.indicateurCoemprunteur,
      codePeriodeAchat: form.codePeriodeAchat,
      indicateurAcceptationOffreAchat: form.indicateurAcceptationOffreAchat,
      numeroTelephone: form.numeroTelephone,
      disponibiliteAppel: form.disponibiliteAppel,
      courriel: form.courriel,
      codePostal: form.codePostal,
      langue: form.langue,
      notes: form.notes,
      dateLimiteObtentionFinancement: form.dateAcceptationOffreAchat,
      dateAcceptationOffreAchat: form.dateLimiteObtentionFinancement,
      localisationRepresentant: form.localisationRepresentant
    });

    if (mode !== 'cancel') {
      dispatch(new GoNextPage());
    } else {
      dispatch(new GoToPage('accueil'));
    }
  }

  @Action(SubmitOctroi)
  submitOctroi({ getState, patchState }) {
    const state = getState();

    const octroi = {
      indicateurCoemprunteur: state.indicateurCoemprunteur,
      codePeriodeAchat: state.codePeriodeAchat,
      indicateurAcceptationOffreAchat: state.indicateurAcceptationOffreAchat,
      numeroTelephone: state.numeroTelephone,
      disponibiliteAppel: state.disponibiliteAppel,
      courriel: state.courriel,
      codePostal: state.codePostal,
      moyenCommunication: 'telephone',
      langue: state.langue,
      notes: state.notes,
      dateLimiteObtentionFinancement: state.dateAcceptationOffreAchat,
      dateAcceptationOffreAchat: state.dateLimiteObtentionFinancement
    };

    const postOctroi =
      state.localisationRepresentant !== null
        ? this._octroiApiService.postOctroiWithLocalisation(octroi, state.localisationRepresentant.id)
        : this._octroiApiService.postOctroi(octroi);

    return postOctroi.toPromise().then(data => {
      patchState({
        lastOctroi: data
      });
    });
  }

  @Action(ResetState)
  resetState({ patchState, getState }) {
    const state = getState();
    patchState({
      steps: defaultState.steps,
      currentStep: state.firstStep,
      indicateurCoemprunteur: null,
      codePeriodeAchat: null,
      numeroTelephone: null,
      disponibiliteAppel: [],
      courriel: null,
      codePostal: null,
      langue: null,
      notes: null,
      indicateurAcceptationOffreAchat: null,
      dateLimiteObtentionFinancement: null,
      dateAcceptationOffreAchat: null,
      localisationRepresentant: null
    });
  }

  @Action(MarkAsCompleted)
  markAsCompleted({ patchState }) {
    patchState({
      isCompleted: true
    });
  }

  @Action(SetFirstHomeStep)
  setFirstHomeStep({ patchState }, { step }: SetFirstHomeStep) {
    patchState({
      firstStep: step,
      currentStep: step
    });
  }

  @Action(FakeOctroiDebug)
  fakeOctroiDebug({ patchState }) {
    patchState({
      lastOctroi: {},
      isCompleted: true
    });
  }
}
